import { Modal, notification } from 'antd';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '@/services/msal/authConfig';
import { useEffect } from 'react';
import { COMMON, MAIN } from '@/configs/main.config';
import { Auth } from '@/services/auth.service';
import { BusEvent, EBusEvent } from '@/services/bus-event/bus-event.service';
import { Dynamics } from '@/services/dynamics/dynamics';
import { DynamicsV2 } from '@/services/dynamics/dynamics-v2';
import {
  ESolgariCallType,
  EStatusCode,
} from '@/services/dynamics/interfaces/api/phone-call.interface';
import { logger } from '@/services/logger.service';
import { ISession, RemoteUserType, SipJs } from '@/services/sip-js.service';
import { State } from '@/services/state.service';
import { EPresenceStatusApi } from '@/shared/enums/presence.enum';
import { ECallStatus } from '@/shared/enums/ringing-status.enum';
import ringtoneInbound from '../public/audio/awesome_2d_sound.mp3';
import ringtoneOutbound from '../public/audio/british_phone.mp3';
import { useStore } from '@/stores';
import { EModeValue } from '@/services/dynamics/enums/mode.enum';
import {
  directionCreateSession,
  ICallTemplateData,
} from '@/services/dynamics/interfaces/v2/create-session.interface';
import { IEntity } from '@/shared/interfaces/entity.interface';
import { filter } from 'rxjs';
import { CALL_PAGE_TYPE, CALL_PAGE_STATUS } from '@/shared/constants/callPage';
import { LOCAL_STORAGE } from '@/shared/constants/localStorage';
import { EEntity } from '@/services/dynamics/enums/entity.enum';
import { SolgariApiService } from '@/services/solgari-api/solgari-api.service';
import { USER_TYPE } from '@/shared/constants/user';

const initAudio = () => {
  const audioRingIB = new Audio(ringtoneInbound);
  const audioRingOB = new Audio(ringtoneOutbound);
  audioRingIB.loop = true;
  audioRingOB.loop = true;

  // We need to mute audio elements in order to prevent so-called Ghost calls
  // When you put on your headset this could autoplay audio element on focused Iframe
  return {
    playIB: () => {
      audioRingIB.muted = false;
      return audioRingIB.play();
    },
    playOB: () => {
      audioRingOB.muted = false;
      return audioRingOB.play();
    },
    pauseAll: () => {
      audioRingOB.muted = true;
      audioRingIB.muted = true;
      audioRingOB.pause();
      audioRingIB.pause();
    },
  };
};

type usePhoneCallsProps = {
  setIsLogged: (isLogged: boolean) => void;
  setViewForPlugin: (view: string, status: string) => void;
  navigate: { current: (to: string) => void };
  checkAndOpenIfPluginMinimized: (isMinimized?: boolean) => void;
  runLogin: (token?: string) => void;
  currentTabId: string;
};

export const usePhoneCalls = ({
  setIsLogged,
  setViewForPlugin,
  navigate,
  checkAndOpenIfPluginMinimized,
  runLogin,
  currentTabId,
}: usePhoneCallsProps) => {
  const DEV_MODE = localStorage.getItem(MAIN.LS_IS_DEV);
  const store = useStore();
  const {
    settingsStore: { setInitializationState, initializationState },
    callStore: { setCallPage, setContacts, setActiveContact, clearCallNote },
    chatsStore: { getChatsFromApi },
  } = store;
  const { instance } = useMsal();

  const maximizeCallPage = () => {
    checkAndOpenIfPluginMinimized(true);
    setCallPage({ page: CALL_PAGE_TYPE.FULL });
  };

  const getUserType = async (phoneNumber: string) => {
    const agents = await SolgariApiService.getListOfAgents();
    const isInternal = agents.some(
      (agent) => agent.extensionId.toString() === phoneNumber,
    );
    const userType: RemoteUserType = isInternal
      ? USER_TYPE.INTERNAL
      : USER_TYPE.EXTERNAL;
    return userType;
  };

  const createCallSession = async (entity: IEntity, direction: ECallStatus) => {
    if (Dynamics.getVersion() === 1) {
      return;
    }

    const { isDialerActive: isDialer } = State.state$.getValue();
    const { id } = await Dynamics.savePhoneCallNote({
      desc: '',
      entity,
      timer: { minutes: 0 },
      callState: EStatusCode.OPEN,
      callType: isDialer ? ESolgariCallType.DIALER : ESolgariCallType.REGULAR,
    });
    State.setState({ callNoteId: id });
    BusEvent.sendEvent({ event: EBusEvent.UPD_HISTORY });

    const tempData: ICallTemplateData = {
      phonenumber: entity?.initialPhone,
      subject: `Call with ${entity?.fullName} (${entity?.initialPhone})`,
    };

    const item = {
      id: entity.id,
      entityType: entity.type,
      name: entity.fullName,
    };

    if (direction === ECallStatus.RINGING_IN) {
      tempData.from = [item];
      tempData.directioncode = directionCreateSession.FROM;
    }

    if (direction === ECallStatus.RINGING_OUT) {
      tempData.to = [item];
      tempData.directioncode = directionCreateSession.TO;
    }

    DynamicsV2.createCallSession('call', id, tempData, item, maximizeCallPage);
  };

  const checkAndOpenByPending = async () => {
    const mode = await Dynamics.getMode();
    if (mode === EModeValue.DOCK) {
      setViewForPlugin('show', 'show');
    } else {
      setViewForPlugin('hide', 'show');
    }
  };

  const { playIB, playOB, pauseAll } = initAudio();

  let isTokenExpiredModalVisible = false;

  const subscribeToRingingStatuses = () => {
    const subBus$ = BusEvent.getBus$().subscribe((value) => {
      if (!value) {
        return;
      }

      const { event } = value;

      switch (event) {
        case EBusEvent.HUB_TOKEN_ERR: {
          setInitializationState(() => {
            return {
              percent: initializationState.percent,
              prompt: {
                ...initializationState.prompt,
                sipLogin: { text: "Can't get hub token", color: 'danger' },
              },
            };
          });

          notification.error({
            message: "Can't get solgari hub token from PROXY API server",
            placement: 'bottomRight',
          });
          break;
        }

        case EBusEvent.DYN_TOKEN_ERR:
        case EBusEvent.DYN_VERIFIED_FAILED: {
          setInitializationState(() => {
            return {
              percent: initializationState.percent,
              prompt: {
                ...initializationState.prompt,
                dynLogin: {
                  text: "Can't get dynamics 365 token",
                  color: 'danger',
                },
              },
            };
          });

          notification.error({
            message: "Can't get dynamics 365 token",
            placement: 'bottomRight',
          });
          break;
        }

        case EBusEvent.SIP_LOGIN_FAILED: {
          setInitializationState(() => {
            return {
              percent: 80,
              prompt: {
                ...initializationState.prompt,
                sipLogin: { text: 'Sip login failed', color: 'danger' },
              },
            };
          });

          notification.error({
            message: "Can't login to SIP account",
            placement: 'bottomRight',
          });
          break;
        }

        case EBusEvent.DYN_NEEDED_POPUP: {
          setInitializationState(() => {
            return {
              percent: initializationState.percent,
              prompt: {
                ...initializationState.prompt,
                sipLogin: { text: 'Stop initialization', color: 'warning' },
                dynLogin: {
                  text: 'Allow to open popup in your browser and try it again please',
                  color: 'danger',
                },
              },
            };
          });

          notification.error({
            message: "Can't pass verification in Dynamics 365",
            placement: 'bottomRight',
          });
          break;
        }

        case EBusEvent.SIP_LOGIN_SUCCESS: {
          setIsLogged(true);
          getChatsFromApi();

          const cachedActiveTabId = localStorage.getItem('SOLGARI_TAB_ID');
          if (cachedActiveTabId && cachedActiveTabId !== currentTabId) {
            navigate.current('/inactive');
          } else {
            navigate.current('/dial-pad');
          }

          setInitializationState(() => {
            return {
              percent: 100,
              prompt: {
                ...initializationState.prompt,
                sipLogin: { text: 'Sip user registered', color: 'success' },
              },
            };
          });
          break;
        }

        case EBusEvent.APP_LOGOUT: {
          Auth.logOut();
          navigate.current('/login');
          setViewForPlugin('show', 'hide');
          setIsLogged(false);
          break;
        }

        case EBusEvent.TOKEN_EXPIRED: {
          if (isTokenExpiredModalVisible) {
            break;
          }

          const activeSessions = SipJs.sessions;
          if (activeSessions && activeSessions.length > 0) {
            logger.error(
              '[AUTH] Token expired but user has active call session',
            );
            break;
          }

          SipJs.pushNotification(EPresenceStatusApi.BUSY, true);
          localStorage.setItem(
            LOCAL_STORAGE.LS_MANUAL_PRESENCE,
            EPresenceStatusApi.BUSY,
          );
          isTokenExpiredModalVisible = true;
          Modal.error({
            title: 'Session Expired',
            content: 'Your session has expired. Please log in again.',
            okText: 'OK',
            onOk: async () => {
              try {
                const response = await instance.acquireTokenPopup({
                  ...loginRequest(),
                });
                const token = response.accessToken;

                if (token) {
                  Auth.saveDynToken(token);
                  Auth.saveMSALToken(token);

                  runLogin(token);
                  SipJs.pushNotification(EPresenceStatusApi.AVAILABLE, true);
                  localStorage.setItem(
                    LOCAL_STORAGE.LS_MANUAL_PRESENCE,
                    EPresenceStatusApi.AVAILABLE,
                  );
                }
              } catch {
                window.location.reload();
              } finally {
                isTokenExpiredModalVisible = false;
              }
            },
            afterClose: () => {
              isTokenExpiredModalVisible = false;
            },
          });
          break;
        }

        default:
          break;
      }
    });

    const ringingStatus$ = SipJs.callStatus$.subscribe(
      async (callStatus: ECallStatus) => {
        logger.log('Call status event: ', callStatus);
        const currentCallNote = store.callStore.callNote;
        const wrapUpSession = store.callStore.wrapUpSession;
        const activeContact = store.callStore.activeContact;
        const session: ISession =
          SipJs?.sessions[0] || SipJs?.lastTerminatedSession || wrapUpSession;
        const { wrapUpQueue } = State.state$.getValue();

        if (callStatus === ECallStatus.PENDING) {
          const callPage = wrapUpQueue?.time
            ? {
                page: CALL_PAGE_TYPE.FULL,
                status: CALL_PAGE_STATUS.WRAPUP,
              }
            : {
                page: CALL_PAGE_TYPE.HIDDEN,
                status: CALL_PAGE_STATUS.INACTIVE,
              };
          setCallPage(callPage);
          pauseAll();

          if (currentCallNote.id && session) {
            const { isDialerActive } = State.state$.getValue();
            const callType = isDialerActive
              ? ESolgariCallType.DIALER
              : ESolgariCallType.REGULAR;
            const user =
              activeContact || session?.remoteUser?.connectedUsers[0];

            const baseNoteObject = {
              desc: currentCallNote.text,
              entity: user,
              timer: session.timer,
              noteId: currentCallNote.id,
              callState: EStatusCode.OPEN,
              callType,
            };
            const noteToSave = currentCallNote.extendedSubject
              ? {
                  ...baseNoteObject,
                  timer: session.conferenceTimer,
                  extendedSubject: currentCallNote.extendedSubject,
                }
              : {
                  ...baseNoteObject,
                  relatedInfo: currentCallNote.relatedToData,
                };

            Dynamics.savePhoneCallNote(noteToSave, session);
            if (!wrapUpQueue?.time) {
              clearCallNote();
              setActiveContact(null);
            }
          }

          if (!SipJs.conference) State.setState({ activeEntities: [] });
          checkAndOpenByPending();
          DynamicsV2.removeSessionByConnectedId('call');
          return;
        }

        if (callStatus === ECallStatus.RINGING_OUT) {
          const idleSession: ISession = SipJs.getSessionByType('idle');
          const phoneNumber: string = idleSession.participants.remote.phone;
          const userType: RemoteUserType = await getUserType(phoneNumber);

          if (SipJs.conference) {
            State.setState({
              activeEntities: [
                {
                  ...COMMON.UNKNOWN_ENTITY,
                  ...SipJs.conference.customer,
                },
              ],
            });
          } else {
            setCallPage({
              page: CALL_PAGE_TYPE.FULL,
              status: CALL_PAGE_STATUS.OUTBOUND,
            });
          }

          const { activeEntities } = State.state$.getValue();
          idleSession.setRemoteUser(userType, activeEntities);

          checkAndOpenIfPluginMinimized();
          playOB();
          setContacts(activeEntities);
          createCallSession(activeEntities[0], ECallStatus.RINGING_OUT);
          return;
        }

        if (callStatus === ECallStatus.RINGING_IN) {
          const idleSession = SipJs.getSessionByType('idle');
          const { wrapUpQueue } = State.state$.getValue();
          const presence = SipJs.getMySelfPresence();
          const phoneNumber = idleSession.participants.remote.phone;
          const userType = await getUserType(phoneNumber);
          const basicEntities = SipJs.conference
            ? [
                {
                  ...COMMON.UNKNOWN_ENTITY,
                  ...SipJs.conference.customer,
                },
              ]
            : phoneNumber.length < 5
              ? [
                  {
                    ...COMMON.UNKNOWN_ENTITY,
                    initialPhone: phoneNumber,
                    fullName: idleSession.participants.remote.name,
                  },
                ]
              : [
                  {
                    ...COMMON.UNKNOWN_ENTITY,
                    initialPhone: phoneNumber,
                  },
                ];

          // Specific block for 1 customer
          if (idleSession.xCaseNumber != null) {
            Dynamics.searchAndOpenRecordWrapper(
              EEntity.CASE,
              `?$select=title&$filter=contains(gnb_casenumber,'${idleSession.xCaseNumber}')`,
              false,
            );
          }

          if (idleSession.xSerialNumber != null) {
            Dynamics.searchAndOpenRecordWrapper(
              EEntity.INSTALLED_MACHINE,
              `?$select=new_name&$filter=contains(new_serialnumbercit,'${idleSession.xSerialNumber}')`,
              false,
            );
          }
          // if (!found) {
          //   Dynamics.searchAndOpenRecordWrapper(
          //     EEntity.CASE,
          //     `?$select=title&$filter=contains(gnb_phonenumbernumbersonly,'${phoneNumber}' OR contains(gnb_dealerphonenumbernumbersonly,'${phoneNumber}')`,
          //     false,
          //   );
          //   found = true;
          // }
          //End of specific block

          if (
            wrapUpQueue?.isWorking ||
            presence?.status === EPresenceStatusApi.WRAP_UP
          ) {
            SipJs.declineCall(idleSession.id, {
              status_code: 486,
              reason_phrase: 'In Wrap-up',
            });
            return;
          }

          setContacts(basicEntities);

          setCallPage({
            page: CALL_PAGE_TYPE.FULL,
            status: CALL_PAGE_STATUS.INBOUND,
          });
          createCallSession(basicEntities[0], ECallStatus.RINGING_IN);
          State.setState({
            activeEntities: basicEntities,
          });
          idleSession.setRemoteUser(userType, basicEntities);
          playIB();

          Dynamics.searchEntityByCalls({
            phone: phoneNumber,
            isExactPhoneSearch: true,
          }).then((entities) => {
            setContacts((entities?.length && entities) || basicEntities);
            if (SipJs.sessions.length) {
              State.setState({
                activeEntities: (entities?.length && entities) || basicEntities,
              });
              idleSession.setRemoteUser(
                userType,
                (entities?.length && entities) || basicEntities,
              );
              if (!SipJs.conference) {
                setActiveContact(entities?.[0] || basicEntities[0]);
              }
            }
          });
          checkAndOpenIfPluginMinimized();
          return;
        }
        if (callStatus === ECallStatus.CONNECTED) {
          SipJs.setCodeForServer(EPresenceStatusApi.ON_A_CALL);
        }
        // DISCONNECTED means that were several session and one has been disconnected
        if (
          callStatus === ECallStatus.CONNECTED ||
          callStatus === ECallStatus.DISCONNECTED
        ) {
          setCallPage({
            page: CALL_PAGE_TYPE.FULL,
            status: CALL_PAGE_STATUS.ACTIVE,
          });
          setViewForPlugin('show', 'show');
          pauseAll();
        }
      },
    );

    return function cleanup() {
      subBus$.unsubscribe();
      ringingStatus$.unsubscribe();
    };
  };

  useEffect(() => {
    const isLoggedDynamics = Auth.isLogged();

    navigate.current('/login');
    setViewForPlugin('show', 'hide');

    const cachedActiveTabId = localStorage.getItem(LOCAL_STORAGE.LS_TAB_ID_KEY);

    if (cachedActiveTabId && cachedActiveTabId !== currentTabId) {
      navigate.current('/inactive');
      return;
    }

    if (!DEV_MODE && !isLoggedDynamics) {
      runLogin();
    }

    if (!DEV_MODE && isLoggedDynamics) {
      const token = Auth.getDynJwtToken();
      runLogin(token);
    }

    const unSub = subscribeToRingingStatuses();

    const sub$ = SipJs.callStatus$
      .pipe(filter((status) => status === ECallStatus.PENDING))
      .subscribe(() => {
        const { wrapUpQueue } = State.state$.getValue();
        if (!wrapUpQueue || wrapUpQueue.isWorking || SipJs.conference) {
          return;
        }

        State.setState({
          wrapUpQueue: {
            ...wrapUpQueue,
            isWorking: true,
          },
        });
      });

    setViewForPlugin('show', 'show');

    return () => {
      unSub();
      sub$.unsubscribe();
    };
  }, []);

  return {
    maximizeCallPage,
  };
};
